import {pathOps} from "./pathOps";
import {Principal} from '@dfinity/principal';
import {HttpAgent, Identity} from '@dfinity/agent';
import {CompilerService} from './compiler/CompilerService';
import {AssetManager} from "@dfinity/assets";
import {Project} from "../UserCanister/candid/main.did";
import {pathUtils} from "./file/pathUtils";
import {Buffer} from 'buffer';
import {safeJsonStringify} from "../Admin/ApiUtils";

// Type definitions
interface ProjectFile {
    name: string;
    content: string;
}

interface CanisterInfo {
    principal: Principal;
    canisterType: string;
}

interface PackageInfo {
    name?: string;
    repo?: string;
    version?: string;
    dir?: string[][];
}

interface BundlerOutput {
    type: 'Buffer';
    data: number[];
}

interface BundlerResponse {
    success: boolean;
    output: Record<string, BundlerOutput>;
}

interface BundlerJobResponse {
    success: boolean;
    jobId: string;
    status: string;
    statusUrl: string;
    downloadUrl: string;
    estimatedTime: string;
}

interface BundlerStatusResponse {
    status: 'queued' | 'processing' | 'completed' | 'failed';
    progress?: number;
    error?: string;
    result?: any;
}

interface EncodeCandidResponse {
    encodedHex: string;       // Hex string representation
    encodedBase64: string;    // Base64 string representation
    binaryData: Uint8Array;   // Binary data as Uint8Array
    originalString: string;   // The original Candid string
}

interface EncodeCandidOptions {
    type?: string;            // Optional type information
}

type CompilationStatus = 'idle' | 'compiling' | 'deploying';


// Parameters for backend deployment
interface BackendDeployParams {
    files: Array<ProjectFile>;
    activeFile: string;
    selectedActorFile?: string;
    project: Project;
    userPrincipal: Principal;
    actor: any;
    userCanisters: Array<CanisterInfo>;
    compilerService: CompilerService;
    onError: (msg: string) => void;
    onSuccess: (msg: string) => void;
    onProjectUpdate?: () => Promise<void>;
    setCompilationStatus: (status: CompilationStatus) => void;
    setDeploymentError: (error: string | null) => void;
    deploymentId: string;
    versionId?: string;
    initArgs?: string;
    installMode?: string;
    targetCanisterId?: string;
    identity: Identity | Promise<Identity>; // ADD THIS LINE
}

// Parameters for frontend deployment
interface FrontendDeployParams {
    files: Array<ProjectFile>;
    project: Project;
    userPrincipal: Principal;
    actor: any;
    userCanisters: Array<CanisterInfo>;
    onError: (msg: string) => void;
    onSuccess: (msg: string) => void;
    setCompilationStatus: (status: CompilationStatus) => void;
    setDeploymentError: (error: string | null) => void;
    identity: Identity | Promise<Identity>;
    onProjectUpdate?: () => Promise<void>;
    deploymentId: string;
    versionId?: string;
    projectType?: string; // 🔥 ADD THIS LINE
}

const host = process.env.DFX_NETWORK === "ic"
    ? "https://icp0.io"
    : "http://127.0.0.1:4943";

const CHUNK_SIZE = 1900000; // Slightly below 2MB limit

function getContentType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'html':
            return 'text/html';
        case 'js':
            return 'application/javascript';
        case 'css':
            return 'text/css';
        case 'json':
            return 'application/json';
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'svg':
            return 'image/svg+xml';
        default:
            return 'application/octet-stream';
    }
}


export const handleBackendDeploy = async (params: BackendDeployParams) => {
    try {
        const {
            files,
            activeFile,
            selectedActorFile,  // ADD THIS LINE
            project,
            userPrincipal,
            actor,
            userCanisters,
            compilerService,
            deploymentId,
            versionId,
            initArgs,
            installMode = 'reinstall',
            targetCanisterId,
        } = params;

        const motokoFiles = files
            .filter((file) => file.name.endsWith('.mo'))
            .map(file => ({
                name: file.name,
                content: file.content
            }));

        const targetFile = selectedActorFile || activeFile;

        const mainFileForCompilation = selectedActorFile && selectedActorFile.endsWith('.mo')
            ? selectedActorFile
            : targetFile.endsWith('.mo')
                ? targetFile
                : motokoFiles[0].name;


        console.log("handleBackendDeploy called with:", {
            activeFile,
            selectedActorFile,
            targetFile,
            initArgs,
            installMode,
            targetCanisterId,
            deploymentId,
            versionId,
            filesCount: files.length,
            motokoFilesFound: files.filter(f => f.name.endsWith('.mo')).map(f => f.name)
        });

        let deploymentRecordId = deploymentId;
        let deploymentUpdateId;

        // Find target canister - prioritize targetCanisterId if provided
        let targetCanister;

        if (targetCanisterId) {
            // Use explicit target canister ID from dialog selection
            console.log(`Using explicit target canister from dialog: ${targetCanisterId}`);
            targetCanister = userCanisters.find(canister =>
                canister.principal.toText() === targetCanisterId
            );

            if (!targetCanister) {
                throw new Error(`Target canister ${targetCanisterId} not found in user canisters`);
            }

            console.log(`Found target canister: ${targetCanister.name || targetCanister.principal.toText()}`);
        } else {
            // Fall back to existing logic for backward compatibility
            console.log("No explicit target canister provided, using project-based selection");
            targetCanister = userCanisters.find(
                (canister) =>
                    project.canisters.some((projectCanister) =>
                        projectCanister.toText() === canister.principal.toText()
                    ) &&
                    canister.canisterType === 'backend'
            );
        }

        if (!targetCanister) {
            const errorMsg = targetCanisterId
                ? `Target canister ${targetCanisterId} not found`
                : 'No backend canister found in this project';
            throw new Error(errorMsg);
        }

        console.log(`Final target canister selected: ${targetCanister.principal.toText()}`);

        if (!deploymentRecordId) {
            try {
                // Create deployment record
                deploymentRecordId = await createDeploymentRecord(
                    actor,
                    project.id,
                    `Backend: ${project.name}`,
                    targetCanister.principal,
                    "ic", // Default to IC network
                    versionId,
                    "backend"
                );

                console.log(`Created new deployment record: ${deploymentRecordId}`);
            } catch (err) {
                console.error("Failed to create deployment record:", err);
                // Continue with deployment even if record creation fails
            }
        }

        console.log("Raw value in handleBackendDeploy", initArgs);
        console.log("Raw value in handleBackendDeploy - params.initArgs", params.initArgs);


        if (motokoFiles.length === 0) {
            throw new Error('No Motoko files found in the project');
        }

        // Get main file
        const mainFile = targetFile.endsWith('.mo') ? targetFile : motokoFiles[0].name;
        const actorName = pathOps.getFileName(mainFile).split('.')[0];

        console.log("Compilation target debug:", {
            targetFile,
            mainFile,
            actorName,
            targetFileEndsWith_mo: targetFile.endsWith('.mo'),
            firstMotokoFile: motokoFiles[0]?.name
        });

        // Process package dependencies
        const packageDependencies = Array.isArray(project.motokoPackages?.[0])
            ? project.motokoPackages[0]
            : [];

        const packageConfig = packageDependencies
            .filter(pkg => {
                if (!pkg || typeof pkg !== 'object') return false;
                if (pkg.name === 'scc' || pkg.name === 'icip') return false;
                if (pkg.name === 'base' && pkg.version === 'master') {
                    pkg.version = '0.14.9';
                }
                const name = pkg.name?.trim();
                const repo = pkg.repo?.trim();
                const version = pkg.version?.trim();
                return Boolean(name && repo && version);
            })
            .map(pkg => ({
                name: pkg.name.trim(),
                repo: pkg.repo.trim(),
                version: pkg.version.trim(),
                dir: pkg.dir?.[0]?.length ? pkg.dir[0].join(', ') : null,
            }));

        let compilationOutput;

        console.log("About to compile with:", {
            mainFile,
            actorName,
            motokoFilesCount: motokoFiles.length,
            packageConfigCount: packageConfig.length
        });

        try {
            compilationOutput = await compilerService.compileFiles(
                motokoFiles,
                mainFile,
                {packages: packageConfig, mode: installMode}
            );

            console.log("Compilation successful:", {
                hasWasm: !!compilationOutput.wasm,
                hasCandid: !!compilationOutput.candid,
                wasmSize: compilationOutput.wasm?.length,
                candidPreview: compilationOutput.candid?.substring(0, 200) + "..."
            });
        } catch (error: any) {
            if (error?.diagnostics) {
                throw error;
            }
            throw new Error(error instanceof Error ? error.message : String(error));
        }

        // Save generated files
        const wasmDir = "wasms";
        const candidDir = pathUtils.normalizePath(`${project.name}/src/frontend/candid`);
        const versionParam = versionId ? [versionId] : [];

// Add this to your handleBackendDeploy function in DeployHandler.ts
// Replace the existing WASM storage section with this enhanced version:

// Save WASM - Store large files in asset canister, small files in project storage
        try {
            const wasmSize = compilationOutput.wasm.length;
            console.log(`WASM size: ${wasmSize} bytes (${(wasmSize / (1024 * 1024)).toFixed(2)} MB)`);

            if (wasmSize > CHUNK_SIZE) {
                // Store large WASM files in the asset canister
                console.log("WASM file too large for project storage, storing in asset canister...");

                // Check if project has any canisters at all
                if (!project.canisters || project.canisters.length === 0) {
                    console.warn('Project has no canisters attached - cannot store WASM in asset canister');
                    console.log('Large WASM file will not be stored as artifact, but deployment will continue...');
                    return; // Skip asset storage attempt
                }

                // Find the frontend canister specifically attached to this project
                const frontendCanister = userCanisters.find(
                    (canister) =>
                        project.canisters.some((projectCanister) =>
                            projectCanister.toText() === canister.principal.toText()
                        ) &&
                        canister.canisterType === 'frontend'
                );

                console.log(`Project has ${project.canisters.length} canisters attached`);
                console.log(`Found ${userCanisters.filter(c => c.canisterType === 'frontend').length} frontend canisters in user's canisters`);
                console.log(`Frontend canister attached to project: ${frontendCanister ? frontendCanister.principal.toText() : 'None'}`);

                if (frontendCanister) {
                    try {
                        // Use the identity from the deployment parameters
                        if (!params.identity) {
                            throw new Error('Identity not provided in backend deployment parameters');
                        }

                        // Setup asset manager for the frontend canister
                        const agent = new HttpAgent({
                            identity: await Promise.resolve(params.identity),
                            host: host
                        });

                        if (host.includes('localhost') || host.includes('127.0.0.1')) {
                            await agent.fetchRootKey();
                        }

                        const assetManager = new AssetManager({
                            canisterId: frontendCanister.principal,
                            agent: agent,
                        });

                        // Create WASM file path in asset canister following project structure
                        // Ensure clean path without double slashes
                        const wasmPath = `projects/${project.id}/wasms/${actorName}.wasm`;

                        console.log(`Storing large WASM file in asset canister at: ${wasmPath}`);
                        console.log(`Project ID: ${project.id}, Actor name: ${actorName}`);

                        // Store the WASM file in the asset canister
                        await assetManager.store(
                            new Uint8Array(compilationOutput.wasm),
                            {
                                fileName: wasmPath,
                                contentType: 'application/wasm'
                            }
                        );

                        console.log(`Successfully stored large WASM file (${wasmSize} bytes) in asset canister at: ${wasmPath}`);

                        // Create a reference file in project storage to indicate where the WASM is stored
                        const wasmRef = {
                            stored_in: 'asset_canister',
                            canister_id: frontendCanister.principal.toText(),
                            path: wasmPath,
                            size: wasmSize,
                            actor_name: actorName,
                            stored_at: new Date().toISOString()
                        };

                        try {
                            const referenceContent = {Text: JSON.stringify(wasmRef, null, 2)};
                            const referenceFileName = `${actorName}.wasm.ref`;

                            // Store reference file in project storage
                            const existingRef = await actor.readCodeArtifact(
                                userPrincipal,
                                project.id,
                                wasmDir,
                                referenceFileName,
                                versionParam
                            );

                            if ('ok' in existingRef) {
                                await actor.updateCodeArtifact(
                                    userPrincipal,
                                    project.id,
                                    referenceFileName,
                                    referenceContent,
                                    wasmDir,
                                    versionParam
                                );
                            } else {
                                await actor.createCodeArtifact(
                                    userPrincipal,
                                    project.id,
                                    referenceFileName,
                                    referenceContent,
                                    'application/json',
                                    'json',
                                    wasmDir,
                                    versionParam
                                );
                            }

                            console.log(`Created WASM reference file: ${referenceFileName}`);
                        } catch (refError) {
                            console.warn('Failed to create WASM reference file:', refError);
                            // Continue - the WASM is still stored, just no reference
                        }

                    } catch (assetError) {
                        console.error('Failed to store WASM in asset canister:', assetError);
                        console.log('Large WASM file will not be stored as artifact, but deployment will continue...');
                    }
                } else {
                    console.warn('No frontend canister found attached to this project');
                    console.log('Available canisters in project:', project.canisters.map(c => c.toText()));
                    console.log('User\'s frontend canisters:', userCanisters
                        .filter(c => c.canisterType === 'frontend')
                        .map(c => c.principal.toText())
                    );
                    console.log('To store large WASM files, attach a frontend canister to this project');
                    console.log('Large WASM file will not be stored as artifact, but deployment will continue...');

                    // Optionally, you could show a user-friendly message
                    if (params.onError) {
                        params.onError(`Large WASM file (${(wasmSize / (1024 * 1024)).toFixed(2)} MB) cannot be stored as project artifact because no frontend canister is attached to this project. The deployment will continue, but you won't be able to download the WASM file later. Consider adding a frontend canister to your project for asset storage.`);
                    }
                }
            } else {
                // Store small WASM files directly in project storage (existing logic)
                const existingWasm = await actor.readCodeArtifact(
                    userPrincipal,
                    project.id,
                    wasmDir,
                    `${actorName}.wasm`,
                    versionParam
                );

                if ('ok' in existingWasm) {
                    await actor.updateCodeArtifact(
                        userPrincipal,
                        project.id,
                        `${actorName}.wasm`,
                        {Binary: Array.from(compilationOutput.wasm)},
                        wasmDir,
                        versionParam
                    );
                } else {
                    await actor.createCodeArtifact(
                        userPrincipal,
                        project.id,
                        `${actorName}.wasm`,
                        {Binary: Array.from(compilationOutput.wasm)},
                        'application/wasm',
                        'wasm',
                        wasmDir,
                        versionParam
                    );
                }
                console.log("Small WASM file stored directly in project storage");
            }
        } catch (error) {
            console.error('Failed to save WASM file:', error);
            console.log('Continuing with deployment...');
        }

        // Save Candid file
        if (compilationOutput.candid) {
            const filesToSave = [
                {
                    name: `${actorName}.did`,
                    content: compilationOutput.candid,
                    type: 'text/plain',
                    category: 'candid'
                },
                {
                    name: `${actorName}.did.d.ts`,
                    content: compilationOutput.typescript,
                    type: 'text/typescript',
                    category: 'typescript'
                },
                {
                    name: `${actorName}.did.js`,
                    content: compilationOutput.didJs,
                    type: 'text/javascript',
                    category: 'javascript'
                }
            ];

            // Add JSON schema file if it exists
            if (compilationOutput.jsonSchema) {
                filesToSave.push({
                    name: `${actorName}.json`,
                    content: compilationOutput.jsonSchema,
                    type: 'application/json',
                    category: 'json'
                });
            }

            for (const file of filesToSave) {
                try {
                    const existingFile = await actor.readCodeArtifact(
                        userPrincipal,
                        project.id,
                        candidDir,
                        file.name,
                        versionParam
                    );

                    if ('ok' in existingFile) {
                        await actor.updateCodeArtifact(
                            userPrincipal,
                            project.id,
                            file.name,
                            {Text: file.content},
                            candidDir,
                            versionParam
                        );
                    } else {
                        await actor.createCodeArtifact(
                            userPrincipal,
                            project.id,
                            file.name,
                            {Text: file.content},
                            file.type,
                            file.category,
                            candidDir,
                            versionParam
                        );
                    }
                } catch (error) {
                    console.error(`Failed to save ${file.name}:`, error);
                }
            }
        }

        // Deploy to canister
        const metadata = {
            name: mainFile,
            canisterType: "backend",
            didInterface: compilationOutput.candid ? [compilationOutput.candid] : [],
            stableInterface: compilationOutput.stable ? [compilationOutput.stable] : [],
            project: project.id ? [project.id] : [],
            subType: []
        };

        // Map the installMode string to the proper parameter format for the actor
        const modeParam = installMode ? [`${installMode}`] : ["upgrade"]; // Default to "upgrade"

        // Check WASM file size to determine deployment method
        const wasmSize = compilationOutput.wasm.length;
        console.log(`WASM size: ${wasmSize} bytes (${(wasmSize / (1024 * 1024)).toFixed(2)} MB)`);
        console.log(`Deploying to specific canister: ${targetCanister.principal.toText()}`);

        let deploymentResult;

        if (wasmSize > CHUNK_SIZE) {
            // Use chunked upload for large WASM files
            console.log(`WASM file size (${wasmSize} bytes) exceeds chunk limit (${CHUNK_SIZE} bytes).`);
            console.log("Using chunked upload method...");

            try {
                // Start upload session
                const sessionId = await actor.startWasmUploadSession(
                    project.id,
                    `${actorName}.wasm`,
                    Math.ceil(wasmSize / CHUNK_SIZE),  // Total chunks
                    wasmSize,                          // Total size
                    targetCanister.principal ? [targetCanister.principal] : [],          // Target canister
                    ["backend"],                         // Canister type
                    ["production"]                       // Deployment stage
                );

                if (!('ok' in sessionId)) {
                    throw new Error(`Failed to start upload session: ${sessionId.err}`);
                }

                const uploadSessionId = sessionId.ok;
                console.log(`Upload session created with ID: ${uploadSessionId}`);

                // Upload chunks
                const wasmBytes = Array.from(compilationOutput.wasm);
                let chunkIndex = 0;

                for (let i = 0; i < wasmSize; i += CHUNK_SIZE) {
                    const end = Math.min(i + CHUNK_SIZE, wasmSize);
                    const chunk = wasmBytes.slice(i, end);

                    console.log(`Uploading chunk ${chunkIndex + 1}/${Math.ceil(wasmSize / CHUNK_SIZE)} (${chunk.length} bytes)`);

                    const chunkResult = await actor.uploadWasmChunk(
                        uploadSessionId,
                        chunkIndex,
                        chunk
                    );

                    if (!('ok' in chunkResult)) {
                        throw new Error(`Failed to upload chunk ${chunkIndex}: ${chunkResult.err}`);
                    }

                    console.log(`Chunk ${chunkIndex + 1} uploaded successfully: ${chunkResult.ok.receivedChunks}/${chunkResult.ok.totalChunks}`);
                    chunkIndex++;
                }

                // Finalize the upload
                console.log("Finalizing WASM upload...");
                const finalizeResult = await actor.finalizeWasmUpload(uploadSessionId);

                if (!('ok' in finalizeResult)) {
                    throw new Error(`Failed to finalize WASM upload: ${finalizeResult.err}`);
                }

                console.log("WASM upload finalized successfully: " + finalizeResult.ok);

                // Deploy the stored WASM
                console.log("Deploying stored WASM...");

                if (!initArgs) {
                    // Standard deployment without init args
                    deploymentResult = await actor.deployStoredWasm(
                        project.id,                    // Project ID
                        `${actorName}.wasm`,        // WASM filename
                        targetCanister.principal,      // Target canister (explicit)
                        'backend',                     // Canister type
                        'production',                  // Deployment stage
                        userPrincipal,                 // User principal
                        [metadata],                    // Metadata
                        versionParam,                  // Version parameter
                        modeParam                      // Install mode
                    );
                } else {
                    // Deployment with init args
                    console.log("[DeployHandler] Encoding init args for stored WASM:", initArgs);

                    try {
                        // Call dfxUtils to encode the Candid string
                        const response = await fetch('https://dfxutils.coinnation.io/encodeWithCandid', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                candidString: initArgs
                            }),
                        });

                        if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(`Failed to encode initArgs: ${errorData.error || response.statusText}`);
                        }

                        const result = await response.json();

                        if (!result.success) {
                            throw new Error(`Encoding failed: ${result.error || 'Unknown error'}`);
                        }

                        // Convert the base64 encoded result to a binary array
                        const binaryData = Buffer.from(result.encodedBase64, 'base64');

                        console.log("[DeployHandler] Successfully encoded init args, binary length:", binaryData.length);

                        // Deploy stored WASM without custom init args support (noted limitation)
                        console.log("Warning: Deploying stored WASM without custom init args (not supported yet)");
                        deploymentResult = await actor.deployStoredWasm(
                            project.id,
                            `${actorName}.wasm`,
                            targetCanister.principal,      // Target canister (explicit)
                            'backend',
                            'production',
                            userPrincipal,
                            [metadata],
                            versionParam,
                            modeParam
                        );
                    } catch (encodingError) {
                        console.error("[DeployHandler] Error encoding init args:", encodingError);

                        // Fall back to standard deployment
                        console.warn("[DeployHandler] Falling back to standard deployment without init args");
                        deploymentResult = await actor.deployStoredWasm(
                            project.id,
                            `${actorName}.wasm`,
                            targetCanister.principal,      // Target canister (explicit)
                            'backend',
                            'production',
                            userPrincipal,
                            [metadata],
                            versionParam,
                            modeParam
                        );
                    }
                }
            } catch (chunkingError) {
                console.error("Error in chunked upload process:", chunkingError);
                throw new Error(`Chunked upload failed: ${(chunkingError as Error).message || String(chunkingError)}`);
            }
        } else {
            // Use direct deployment for small WASM files
            console.log(`WASM file size (${wasmSize} bytes) is under the chunk limit. Using direct deployment.`);

            if (!initArgs) {
                deploymentResult = await actor.deployToExistingCanister(
                    targetCanister.principal,          // Target canister (explicit)
                    Array.from(compilationOutput.wasm),
                    'backend',
                    'backend',
                    userPrincipal,
                    [metadata],
                    versionParam,
                    modeParam
                );
            } else {
                // For string initArgs, encode it first using dfxUtils
                console.log("[DeployHandler] Encoding init args:", initArgs);

                try {
                    // Call dfxUtils to encode the Candid string
                    const response = await fetch('https://dfxutils.coinnation.io/encodeWithCandid', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            candidString: initArgs
                        }),
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(`Failed to encode initArgs: ${errorData.error || response.statusText}`);
                    }

                    const result = await response.json();

                    if (!result.success) {
                        throw new Error(`Encoding failed: ${result.error || 'Unknown error'}`);
                    }

                    // Convert the base64 encoded result to a binary array
                    const binaryData = Buffer.from(result.encodedBase64, 'base64');

                    console.log("[DeployHandler] Successfully encoded init args, binary length:", binaryData.length);

                    // Deploy with the encoded init args
                    deploymentResult = await actor.deployBackendWithCustomInit(
                        targetCanister.principal,      // Target canister (explicit)
                        Array.from(compilationOutput.wasm),
                        binaryData, // This will be received as Blob in Motoko
                        [metadata],
                        versionParam,
                        modeParam
                    );
                } catch (encodingError) {
                    console.error("[DeployHandler] Error encoding init args:", encodingError);

                    // Fall back to using the raw string if encoding fails
                    console.warn("[DeployHandler] Falling back to raw string init args");

                    deploymentResult = await actor.deployBackendWithCustomInit(
                        targetCanister.principal,      // Target canister (explicit)
                        Array.from(compilationOutput.wasm),
                        initArgs, // Use the raw string as fallback
                        [metadata],
                        versionParam,
                        modeParam
                    );
                }
            }
        }

        if ('ok' in deploymentResult) {
            params.onSuccess(`Backend deployment successful! Deployed ${motokoFiles.length} Motoko files to canister ${targetCanister.principal.toText()} with ${packageDependencies.length} package dependencies.`);
            params.setDeploymentError(null);

            if (versionId && !deploymentId) {
                try {
                    // Associate canister with this version
                    await actor.updateVersionCanisters(
                        versionId,
                        [targetCanister.principal]
                    );

                    // Create deployment reference for this version
                    const deploymentRef = {
                        id: deploymentId || `deploy-${Date.now()}`,
                        name: "Backend deployment",
                        projectId: project.id,
                        canisterId: targetCanister.principal,
                        status: "deployed",
                        network: "ic", // Adjust based on your environment
                        lastUpdated: BigInt(Date.now() * 1000000) // Convert to nanoseconds
                    };

                    await actor.addDeploymentToVersion(versionId, deploymentRef);
                    console.log(`Deployment associated with version ${versionId}`);
                } catch (versionError) {
                    console.error("Error associating deployment with version:", versionError);
                    // Don't fail the deployment if version association fails
                }
            }

            if (params.onProjectUpdate) {
                await params.onProjectUpdate();
            }

            if (deploymentRecordId) {
                deploymentUpdateId = deploymentRecordId;
            } else if (deploymentId) {
                deploymentUpdateId = deploymentId;
            }

            if ((deploymentId || deploymentUpdateId) && !versionId) {
                await actor.updateDeploymentStatus(
                    deploymentUpdateId,
                    "success"
                );
            } else if ((deploymentId || deploymentUpdateId) && versionId) {
                await actor.updateDeploymentStatusWithVersion(
                    deploymentUpdateId,
                    "success",
                    [versionId]
                );
            }

            return {
                success: true,
                canisterId: targetCanister.principal
            };
        } else {
            if (params.deploymentId && !params.versionId) {
                await params.actor.updateDeploymentStatus(
                    deploymentUpdateId,
                    "failed"
                );
            } else if (params.deploymentId && params.versionId) {
                await params.actor.updateDeploymentStatusWithVersion(
                    deploymentUpdateId,
                    "failed",
                    [params.versionId]
                );
            }
            throw new Error(deploymentResult.err);
        }
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        const diagnostics = (error as any)?.diagnostics || [];

        let detailedMessage = errorMsg;

        if (diagnostics.length > 0) {
            const formattedDiagnostics = diagnostics
                .filter((diag: any) => {
                    // Convert severity to number if it's a string
                    const severity = typeof diag.severity === 'string'
                        ? parseInt(diag.severity, 10)
                        : diag.severity;

                    // Only keep diagnostics with severity === 1
                    return severity === 1;
                })
                .map((diag: any) => {
                    const code = diag.code || "UnknownCode";
                    const msg = diag.message || "Unknown message";
                    const file = diag.source || diag.range?.source || "unknown file";
                    const line = diag.range?.start?.line ?? "?";
                    const char = diag.range?.start?.character ?? "?";
                    return `• [${code}] ${msg} (${file}:${line}:${char})`;
                }).join('\n');

            detailedMessage += `\n\nErrors Found:\n` + formattedDiagnostics;
        }

        params.setDeploymentError(detailedMessage);
        params.onError(detailedMessage);

        return {
            success: false,
            error: detailedMessage
        };
    }
};

export const handleFrontendDeploy = async (params: FrontendDeployParams) => {
    let deploymentUpdateId;

    try {
        const {
            files,
            project,
            userPrincipal,
            actor,
            userCanisters,
            identity,
            deploymentId,
            versionId,
            projectType = 'icpstudio'
        } = params;

        console.log("Frontend deploy received projectType:", projectType);

        let deploymentRecordId = deploymentId;

        if (!deploymentRecordId) {
            try {
                const targetCanister = userCanisters.find(
                    (canister) =>
                        project.canisters.some((projectCanister) =>
                            projectCanister.toText() === canister.principal.toText()
                        ) &&
                        canister.canisterType === 'backend'
                );

                if (targetCanister) {
                    deploymentRecordId = await createDeploymentRecord(
                        actor,
                        project.id,
                        `Frontend: ${project.name}`,
                        targetCanister.principal,
                        "ic",
                        versionId,
                        "frontend"
                    );
                    console.log(`Created new deployment record: ${deploymentRecordId}`);
                }
            } catch (err) {
                console.error("Failed to create deployment record:", err);
            }
        }

        const versionParam = versionId ? [versionId] : [];

        const targetCanister = userCanisters.find(
            (canister) =>
                project.canisters.some((projectCanister) =>
                    projectCanister.toText() === canister.principal.toText()
                ) &&
                canister.canisterType === 'frontend'
        );

        if (!targetCanister) {
            throw new Error('No frontend asset canister found');
        }

        const backendCanister = userCanisters.find(
            (canister) =>
                project.canisters.some((projectCanister) =>
                    projectCanister.toText() === canister.principal.toText()
                ) &&
                canister.canisterType === 'backend'
        );

        const backendCanisterId = backendCanister ? backendCanister.principal.toText() : undefined;

        // Setup asset manager
        const resolvedIdentity = await Promise.resolve(identity);
        const agent = new HttpAgent({
            identity: resolvedIdentity,
            host: host
        });

        if (host.includes('localhost') || host.includes('127.0.0.1')) {
            await agent.fetchRootKey();
        }

        const assetManager = new AssetManager({
            canisterId: targetCanister.principal,
            agent: agent,
        });

        const frontendFiles = filterFrontendFiles(files, project.name);
        
        // Helper to extract content safely
        const getFileContent = (file: any): string => {
            if (typeof file.content === 'string') {
                return file.content;
            }
            if (file.content && typeof file.content === 'object' && 'Text' in file.content) {
                return (file.content as any).Text || '';
            }
            return '';
        };
        
        console.log('📁 Frontend files after filtering:', {
            count: frontendFiles.length,
            files: frontendFiles.map(f => {
                const content = getFileContent(f);
                return { 
                    name: f.name, 
                    contentLength: content.length,
                    hasContent: !!(content && content.trim().length > 0)
                };
            })
        });
        
                // Validate that frontend SOURCE files (not .keep or other intentionally empty files) have content
                const sourceFileExtensions = ['.ts', '.tsx', '.js', '.jsx', '.css', '.html', '.json'];
                const filesWithoutContent = frontendFiles.filter(f => {
                    // Skip intentionally empty files
                    if (f.name.endsWith('.keep') || 
                        f.name.endsWith('.gitkeep') || 
                        f.name.endsWith('.placeholder') ||
                        f.name === '.gitkeep' ||
                        f.name.endsWith('/.keep')) {
                        return false; // Allow these files
                    }
                    
                    // Only validate source files
                    const isSourceFile = sourceFileExtensions.some(ext => f.name.endsWith(ext));
                    if (!isSourceFile) {
                        return false; // Skip non-source files
                    }
                    
                    const content = getFileContent(f);
                    return !content || content.trim() === '';
                });

                if (filesWithoutContent.length > 0) {
                    console.error(`❌ ERROR: ${filesWithoutContent.length} frontend source files have no content:`,
                        filesWithoutContent.map(f => f.name));
                    throw new Error(`Cannot deploy: ${filesWithoutContent.length} source files have empty content: ${filesWithoutContent.map(f => f.name).join(', ')}`);
                }

        // Package.json handling
        let packageJsonFile = frontendFiles.find(file => file.name.endsWith('package.json'));

        if (!packageJsonFile) {
            const hasIndexHtml = frontendFiles.some(f => f.name.endsWith('index.html'));
            const hasReactCode = frontendFiles.some(f =>
                f.content.includes('import React') ||
                f.content.includes('from "react"') ||
                f.content.includes('React.') ||
                f.name.endsWith('.jsx') ||
                f.name.endsWith('.tsx')
            );

            const isStaticProject = hasIndexHtml && !hasReactCode;
            const generateDefaultPackageJson = (projectName: string, isStatic: boolean, backendCanisterId?: string) => {
                if (isStatic) {
                    return {
                        name: projectName.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                        version: "1.0.0",
                        type: "module"
                    };
                } else {
                    return {
                        name: projectName.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                        version: "1.0.0",
                        type: "module",
                        scripts: {
                            dev: "vite",
                            build: "vite build",
                            preview: "vite preview"
                        },
                        dependencies: {
                            react: "^18.2.0",
                            "react-dom": "^18.2.0",
                            ...(backendCanisterId && {
                                "@dfinity/agent": "^0.19.0",
                                "@dfinity/candid": "^0.19.0",
                                "@dfinity/principal": "^0.19.0"
                            })
                        },
                        devDependencies: {
                            "@types/react": "^18.2.0",
                            "@types/react-dom": "^18.2.0",
                            "@vitejs/plugin-react": "^4.0.0",
                            typescript: "^5.0.0",
                            vite: "^5.0.0"
                        }
                    };
                }
            };

            const defaultPackageJson = generateDefaultPackageJson(project.name, isStaticProject, backendCanisterId);
            packageJsonFile = {
                name: `package.json`,
                content: JSON.stringify(defaultPackageJson, null, 2)
            };
        } else {
            const originalPackageJson = JSON.parse(packageJsonFile.content);
            const isCRAProject = originalPackageJson.dependencies?.['react-scripts'];
            const isICPStudioProject = originalPackageJson.dependencies?.['vite'] ||
                originalPackageJson.name?.includes('frontend');

            if (isCRAProject && !isICPStudioProject) {
                const enhancedPackageJson = {
                    ...originalPackageJson,
                    type: "module",
                    dependencies: {
                        ...originalPackageJson.dependencies,
                        "vite": "^5.0.0",
                        "@vitejs/plugin-react": "^4.0.0"
                    },
                    devDependencies: {
                        ...originalPackageJson.devDependencies,
                        "@vitejs/plugin-react": "^4.0.0",
                        "vite": "^5.0.0"
                    },
                    scripts: {
                        ...originalPackageJson.scripts,
                        "dev": "vite",
                        "build": "vite build",
                        "preview": "vite preview"
                    }
                };

                packageJsonFile = {
                    ...packageJsonFile,
                    content: JSON.stringify(enhancedPackageJson, null, 2)
                };
            }
        }

        const packageJson = JSON.parse(packageJsonFile.content);
        const finalFiles = packageJsonFile.name === 'package.json' ?
            [...frontendFiles, packageJsonFile] : frontendFiles;

        // NOTE: cleanFilesForBundler does NOT normalize paths - the bundler's normalizeFilesForProjectType() handles that
        // We just extract content and validate here to avoid double-normalization
        const cleanedFiles = cleanFilesForBundler(finalFiles);

        const {output} = await submitAndWaitForBundle(
            cleanedFiles,
            packageJson,
            project,
            userCanisters,
            host,
            projectType
        );

        // Run both operations in parallel for maximum performance
        await Promise.all([
            saveCanisterHooksInParallel(output, actor, userPrincipal, project, versionParam),
            uploadAssetsWithBatch(assetManager, output, project.id)
        ]);

        if (params.onProjectUpdate) {
            await params.onProjectUpdate();
        }
        params.onSuccess('Frontend deployment successful');
        params.setDeploymentError(null);

        // Handle version association
        if (versionId && !deploymentId) {
            try {
                await actor.updateVersionCanisters(
                    versionId,
                    [targetCanister.principal]
                );

                const deploymentRef = {
                    id: deploymentId || `deploy-${Date.now()}`,
                    name: "Frontend deployment",
                    projectId: project.id,
                    canisterId: targetCanister.principal,
                    status: "deployed",
                    network: "ic",
                    lastUpdated: BigInt(Date.now() * 1000000)
                };

                await actor.addDeploymentToVersion(versionId, deploymentRef);
                console.log(`Frontend deployment associated with version ${versionId}`);
            } catch (versionError) {
                console.error("Error associating frontend deployment with version:", versionError);
            }
        }

        // Update deployment status
        if (deploymentRecordId) {
            deploymentUpdateId = deploymentRecordId;
        } else if (deploymentId) {
            deploymentUpdateId = deploymentId;
        }

        if ((deploymentId || deploymentUpdateId) && !versionId) {
            await actor.updateDeploymentStatus(
                deploymentUpdateId,
                "success"
            );
        } else if ((deploymentId || deploymentUpdateId) && versionId) {
            await actor.updateDeploymentStatusWithVersion(
                deploymentUpdateId,
                "success",
                [versionId]
            );
        }

        return {
            success: true,
            canisterId: targetCanister.principal
        };
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        console.error('Frontend deployment failed:', errorMsg);
        params.setDeploymentError(errorMsg);
        params.onError(errorMsg);

        if (params.deploymentId && !params.versionId) {
            await params.actor.updateDeploymentStatus(
                deploymentUpdateId,
                "failed"
            );
        } else if (params.deploymentId && params.versionId) {
            await params.actor.updateDeploymentStatusWithVersion(
                deploymentUpdateId,
                "failed",
                [params.versionId]
            );
        }
        return {
            success: false,
            error: errorMsg
        };
    }
};

async function saveCanisterHooksInParallel(
    output: Record<string, BundlerOutput>,
    actor: any,
    userPrincipal: Principal,
    project: any,
    versionParam: string[]
) {
    const hookPromises = Object.entries(output)
        .filter(([filename]) => {
            const baseName = filename.split('/').pop() || filename;
            return baseName === 'useCanister.ts' || (baseName.startsWith('use') && baseName.endsWith('Canister.ts'));
        })
        .map(async ([filename, content]) => {
            const baseName = filename.split('/').pop() || filename;
            try {
                console.log(`Saving generated ${baseName} to project artifacts`);

                const fileContent = {Text: new TextDecoder().decode(new Uint8Array(content.data))};
                const wasmDir = `${project.name}/src/frontend/src`;

                const existingFile = await actor.readCodeArtifact(
                    userPrincipal,
                    project.id,
                    wasmDir,
                    baseName,
                    versionParam
                );

                if ('ok' in existingFile) {
                    await actor.updateCodeArtifact(
                        userPrincipal,
                        project.id,
                        baseName,
                        fileContent,
                        wasmDir,
                        versionParam
                    );
                } else {
                    await actor.createCodeArtifact(
                        userPrincipal,
                        project.id,
                        baseName,
                        fileContent,
                        'text/typescript',
                        'typescript',
                        wasmDir,
                        versionParam
                    );
                }
                console.log(`✅ Saved ${baseName}`);
            } catch (error) {
                console.error(`Failed to save ${baseName}:`, error);
                throw error;
            }
        });

    // Run all hook saves in parallel
    if (hookPromises.length > 0) {
        await Promise.all(hookPromises);
    }
}

async function uploadAssetsWithBatch(
    assetManager: AssetManager,
    output: Record<string, BundlerOutput>,
    projectId?: string // Add projectId parameter for more specific filtering
) {
    console.log('📦 Starting optimized batch asset deployment...');

    // STEP 1: Optimized asset clearing - only list if we have a projectId to preserve assets
    // Skip listing entirely if no projectId (faster for first deployments)
    if (projectId) {
        console.log('🗑️ Clearing existing deployable assets from canister...');
        try {
            const existingAssets = await assetManager.list();
            const totalAssets = existingAssets.length;
            
            if (totalAssets > 0) {
                console.log(`Found ${totalAssets} existing assets to process`);
                const clearBatch = assetManager.batch();
                let assetsToDelete = 0;
                let assetsPreserved = 0;

                // Process assets in parallel batches for better performance
                const BATCH_SIZE = 100; // Process 100 assets at a time
                for (let i = 0; i < existingAssets.length; i += BATCH_SIZE) {
                    const batch = existingAssets.slice(i, i + BATCH_SIZE);
                    
                    for (const asset of batch) {
                        const assetKey = typeof asset === 'string' ? asset : asset.key;
                        const shouldPreserve = shouldPreserveAsset(assetKey, projectId);

                        if (shouldPreserve) {
                            assetsPreserved++;
                        } else {
                            clearBatch.delete(assetKey);
                            assetsToDelete++;
                        }
                    }
                }

                if (assetsToDelete > 0) {
                    await clearBatch.commit();
                    console.log(`✅ Cleared ${assetsToDelete} deployable assets, preserved ${assetsPreserved} user assets`);
                } else {
                    console.log(`ℹ️ No deployable assets to clear, preserved ${assetsPreserved} user assets`);
                }
            }
        } catch (clearError) {
            console.error('❌ Failed to clear assets:', clearError);
            // Continue anyway - might be first deployment
        }
    } else {
        console.log('ℹ️ Skipping asset clearing (no projectId provided)');
    }

    // STEP 2: Prepare assets for upload
    const webAssets = Object.entries(output).filter(([filename]) => {
        return !filename.endsWith('.ts') && !filename.endsWith('.tsx');
    });

    console.log(`📤 Preparing ${webAssets.length} assets for upload...`);

    // Pre-process all assets to Uint8Array in parallel (direct processing, no delays)
    const processedAssets = await Promise.all(
        webAssets.map(async ([filename, content]) => {
            // Process directly - no setTimeout needed, Promise.all already parallelizes
            return [
                filename,
                new Uint8Array(content.data),
                getContentType(filename)
            ] as [string, Uint8Array, string];
        })
    );

    // STEP 3: Parallel batch uploads for maximum performance
    const MAX_BATCH_SIZE = 100; // Increased batch size for better throughput
    const CONCURRENT_BATCHES = 3; // Upload 3 batches in parallel
    const totalBatches = Math.ceil(processedAssets.length / MAX_BATCH_SIZE);

    console.log(`🚀 Uploading ${processedAssets.length} assets in ${totalBatches} batches (${CONCURRENT_BATCHES} concurrent)...`);

    // Add CSP and index.html to first batch
    const unrestrictedCSP = createUnrestrictedCSPConfig();
    const hasIndexHtml = Object.keys(output).some(key => key === 'index.html');

    // Helper function to upload a single batch
    const uploadBatch = async (batchIndex: number, batch: [string, Uint8Array, string][]): Promise<void> => {
        const uploadBatchInstance = assetManager.batch();

        // Add CSP and index.html only to first batch
        if (batchIndex === 0) {
            uploadBatchInstance.store(
                new TextEncoder().encode(unrestrictedCSP),
                {
                    fileName: '.ic-assets.json5',
                    contentType: 'application/json'
                }
            );

            if (!hasIndexHtml) {
                const indexHtml = `<!DOCTYPE html>
          <html>
            <head>
              <meta charset="UTF-8" />
              <title>App</title>
              <script type="module" src="/assets/index.js"></script>
            </head>
            <body>
              <div id="root"></div>
            </body>
          </html>`;

                uploadBatchInstance.store(
                    new TextEncoder().encode(indexHtml),
                    {
                        fileName: 'index.html',
                        contentType: 'text/html'
                    }
                );
            }
        }

        // Add batch assets
        for (const [filename, content, contentType] of batch) {
            uploadBatchInstance.store(content, {
                fileName: filename,
                contentType: contentType
            });
        }

        // Commit this batch
        await uploadBatchInstance.commit();
        console.log(`✅ Batch ${batchIndex + 1}/${totalBatches} committed successfully`);
    };

    // Upload batches with controlled concurrency
    const uploadChunk = async (startIndex: number, endIndex: number) => {
        const promises = [];
        for (let i = startIndex; i < endIndex; i++) {
            const startIdx = i * MAX_BATCH_SIZE;
            const endIdx = Math.min(startIdx + MAX_BATCH_SIZE, processedAssets.length);
            const batch = processedAssets.slice(startIdx, endIdx);
            console.log(`📦 Preparing batch ${i + 1}/${totalBatches} (${batch.length} assets)...`);
            promises.push(uploadBatch(i, batch));
        }
        return Promise.all(promises);
    };

    // Process in chunks with controlled concurrency
    for (let i = 0; i < totalBatches; i += CONCURRENT_BATCHES) {
        const endIndex = Math.min(i + CONCURRENT_BATCHES, totalBatches);
        await uploadChunk(i, endIndex);
    }

    console.log('✅ All asset batches uploaded successfully!');
}

function shouldPreserveAsset(assetKey: string, projectId?: string): boolean {
    // Preserve all project-level assets (user uploads)
    if (assetKey.startsWith('projects/')) {
        return true;
    }

    // If projectId is provided, be more specific and only preserve assets for this project
    if (projectId && assetKey.startsWith(`projects/${projectId}/`)) {
        return true;
    }

    // Preserve any .keep files (used for folder structure)
    if (assetKey.endsWith('/.keep')) {
        return true;
    }

    // You can add more preservation rules here if needed
    // For example, preserve certain system files or user-specific configurations

    // Preserve any other assets that might be important
    const preservePatterns = [
        // Add any other patterns you want to preserve
        // Examples:
        // /^user-uploads\//,
        // /^assets\/custom\//,
        // /\.config$/,
    ];

    return preservePatterns.some(pattern => pattern.test(assetKey));
}


export const createDeploymentRecord = async (
    actor: any,
    projectId: string,
    name: string,
    canisterId: Principal,
    network: string = "ic",
    versionId: string | null = null,
    canisterType: string = "unknown"
): Promise<string> => {
    try {
        // Generate unique ID for the deployment
        const deploymentId = `deployment-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 7)}`;

        // Create the deployment reference
        const deploymentRef = {
            id: deploymentId,
            name: name,
            projectId: projectId,
            canisterId: canisterId ? [canisterId] : [],
            status: "pending", // Will be updated to "success" or "failed" after deployment
            network: network,
            lastUpdated: BigInt(Date.now()) * BigInt(1000000) // Convert to nanoseconds
        };

        let result;

        if (versionId) {
            console.log(`Creating versioned deployment for version: ${versionId}`);
            // Add deployment to version
            result = await actor.addDeploymentToVersion(
                versionId,
                deploymentRef
            );
        } else {
            console.log('Creating non-versioned deployment (working copy)');
            // Add deployment to account
            result = await actor.addDeploymentToAccount(
                deploymentId,
                name,
                projectId,
                [canisterId],
                "pending",
                network
            );
        }

        if ('ok' in result) {
            console.log(`Deployment record created: ${deploymentId}`);
            return deploymentId;
        } else {
            console.error(`Failed to create deployment record: ${result.err}`);
            throw new Error(result.err);
        }
    } catch (error) {
        console.error("Error creating deployment record:", error);
        throw error;
    }
};


// Optimized file filtering - minimal logging for performance
const filterFrontendFiles = (files: any[], projectName: string) => {
    const startTime = performance.now();
    
    // Pre-compile patterns for better performance
    const backendExtensions = new Set(['mo', 'did']);
    const excludePatterns = new Set(['.git', 'node_modules', '.dfx', '.vessel', 'target', 'build', 'dist']);
    const importantRootFiles = new Set([
        'package.json', 'package-lock.json', 'yarn.lock', 'tsconfig.json', 'jsconfig.json',
        'vite.config.js', 'vite.config.ts', '.env', '.env.local', '.env.production', '.env.development'
    ]);
    const frontendExtensions = new Set([
        'html', 'htm', 'css', 'scss', 'sass', 'less',
        'js', 'jsx', 'ts', 'tsx', 'mjs',
        'json', 'xml', 'yaml', 'yml',
        'png', 'jpg', 'jpeg', 'gif', 'svg', 'ico', 'webp',
        'woff', 'woff2', 'ttf', 'eot',
        'mp4', 'webm', 'ogg', 'mp3', 'wav',
        'pdf', 'txt', 'md', 'mdx'
    ]);

    const filtered = files.filter(file => {
        const filePath = file.name;
        const fileName = filePath.split('/').pop() || '';
        const extension = fileName.split('.').pop()?.toLowerCase() || '';

        // PRIORITY: Include files in /src/frontend/ directory (ICP Studio structure)
        if (filePath.includes('/src/frontend/')) {
            return true;
        }

        // Include files in /src directory (but exclude backend)
        if (filePath.includes('/src/') && !filePath.includes('/src/backend/')) {
            return true;
        }

        // Include files in /public directory (static assets)
        if (filePath.includes('/public/')) {
            return true;
        }

        // EXCLUDE backend-specific files
        if (filePath.includes('/src/backend/') || filePath.includes('/backend/')) {
            return false;
        }

        // EXCLUDE specific backend file types anywhere in the project
        if (backendExtensions.has(extension)) {
            return false;
        }

        // EXCLUDE common non-frontend files
        for (const pattern of excludePatterns) {
            if (filePath.includes(pattern)) {
                return false;
            }
        }

        // Include important root-level files
        if (importantRootFiles.has(fileName)) {
            return true;
        }

        // INCLUDE everything else that looks like frontend content
        if (frontendExtensions.has(extension)) {
            return true;
        }

        return false;
    });

    const duration = performance.now() - startTime;
    console.log(`🔍 Filtered ${files.length} files → ${filtered.length} frontend files in ${duration.toFixed(2)}ms`);
    
    return filtered;
};

export const findBigIntCulprit = (obj: any, objectName = 'object'): void => {
    const findBigInts = (value: any, path: string[] = []): void => {
        if (typeof value === 'bigint') {
            console.error(`🚨 BIGINT FOUND!`);
            console.error(`Location: ${objectName}.${path.join('.')}`);
            console.error(`Value: ${value}`);
            console.error(`Type: ${typeof value}`);
            console.error('---');
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                findBigInts(item, [...path, `[${index}]`]);
            });
        } else if (typeof value === 'object' && value !== null) {
            Object.keys(value).forEach(key => {
                findBigInts(value[key], [...path, key]);
            });
        }
    };

    console.log(`🔍 Scanning ${objectName} for BigInt values...`);
    findBigInts(obj);
    console.log(`✅ Scan complete for ${objectName}`);
};


async function submitAndWaitForBundle(
    files: any,
    packageJson: any,
    project: any,
    userCanisters: any[],
    host?: string,          // 🆕 MOVED UP (removed backendCanisterId)
    projectType?: string
): Promise<BundlerResponse> {

    // Step 1: Submit the bundling job
    console.log('🚀 Submitting bundling job...');

    // Get canister mappings from localStorage or create intelligent ones
    const canisterMappings = getCanisterMappingsForBundle(
        project.name,
        files,
        userCanisters
    );

    console.log(`📋 Using canister mappings for bundle:`, canisterMappings);

    const initialResponse = await fetch('https://jsbundler.coinnation.io/bundle', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
            files,
            packageJson,
            backendCanisters: canisterMappings, // 🆕 ONLY USE THIS (no backendCanisterId)
            host,
            projectType
        }),
    });

    if (!initialResponse.ok) {
        throw new Error(`Failed to submit bundling job: ${await initialResponse.text()}`);
    }

    const jobResponse: BundlerJobResponse = await initialResponse.json();
    console.log('📋 Bundling job started:', jobResponse);

    if (!jobResponse.success || !jobResponse.jobId) {
        throw new Error('Failed to start bundling job');
    }

    // Step 2: Poll for completion
    const statusUrl = `https://jsbundler.coinnation.io${jobResponse.statusUrl}`;
    const downloadUrl = `https://jsbundler.coinnation.io${jobResponse.downloadUrl}`;

    console.log(`⏳ Polling bundler status (estimated: ${jobResponse.estimatedTime})...`);

    let attempts = 0;
    const maxAttempts = 180; // 15 minutes max (180 attempts * ~5s average polling = ~15 minutes)
    let bundleComplete = false;
    let pollInterval = 2000; // Start with 2 seconds (faster initial polling)
    const maxInterval = 10000; // Max 10 seconds between polls

    while (!bundleComplete && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        try {
            const statusResponse = await fetch(statusUrl);

            if (!statusResponse.ok) {
                console.warn(`Status check failed (attempt ${attempts}): ${statusResponse.status}`);
                // Exponential backoff on errors
                pollInterval = Math.min(pollInterval * 1.5, maxInterval);
                continue;
            }

            const status: BundlerStatusResponse = await statusResponse.json();
            
            // Only log every 5th attempt to reduce noise, or on status changes
            if (attempts % 5 === 0 || status.status === 'completed' || status.status === 'failed') {
                console.log(`📊 Bundler status (attempt ${attempts}): ${status.status}${status.progress ? ` (${status.progress}%)` : ''}`);
            }

            if (status.status === 'completed') {
                bundleComplete = true;
                break;
            } else if (status.status === 'failed') {
                throw new Error(`Bundling failed: ${status.error || 'Unknown error'}`);
            }
            
            // Adaptive polling: faster when processing, slower when queued
            if (status.status === 'processing') {
                pollInterval = Math.max(2000, pollInterval * 0.9); // Speed up when processing
            } else {
                pollInterval = Math.min(pollInterval * 1.2, maxInterval); // Slow down when queued
            }

        } catch (pollError) {
            console.warn(`Status poll error (attempt ${attempts}):`, pollError);
            // Exponential backoff on errors
            pollInterval = Math.min(pollInterval * 1.5, maxInterval);
            if (attempts >= 3) {
                // After 3 failed attempts, throw error
                throw new Error(`Failed to check bundling status: ${pollError}`);
            }
        }
    }

    if (!bundleComplete) {
        throw new Error('Bundling timeout - job did not complete within 15 minutes');
    }

    // Step 3: Download the completed bundle
    console.log('📥 Downloading completed bundle...');
    const downloadResponse = await fetch(downloadUrl);

    if (!downloadResponse.ok) {
        throw new Error(`Failed to download bundle: ${await downloadResponse.text()}`);
    }

    const finalResult: BundlerResponse = await downloadResponse.json();
    console.log('✅ Bundle download successful, output files:', Object.keys(finalResult.output));

    return finalResult;
}


const createUnrestrictedCSPConfig = (): string => {
    return `[
    {
        "match": "**/*",
        "headers": {
            "Content-Security-Policy": "default-src *; script-src * 'unsafe-inline' 'unsafe-eval'; script-src-elem * 'unsafe-inline' 'unsafe-eval'; connect-src *; worker-src * blob: data:; img-src * data: blob:; style-src * 'unsafe-inline'; style-src-elem * 'unsafe-inline'; font-src *; object-src *; base-uri *; frame-ancestors *; form-action *; frame-src * https://jsbundler.coinnation.io; media-src *; manifest-src *;",
            "Permissions-Policy": "",
            "X-Frame-Options": "",
            "Referrer-Policy": "no-referrer-when-downgrade",
            "X-Content-Type-Options": "",
            "X-XSS-Protection": ""
        }
    }
]`;
};

const getProjectCanisterMappings = (projectName: string): Record<string, string> => {
    const mappings: Record<string, string> = {};
    const prefix = `${projectName}:`;

    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            const canisterName = key.substring(prefix.length);
            const canisterId = localStorage.getItem(key);
            if (canisterId) {
                mappings[canisterName] = canisterId;
            }
        }
    }

    console.log(`📋 Retrieved canister mappings for project ${projectName}:`, mappings);
    return mappings;
};

// Helper function to convert mappings to bundle format
const convertMappingsToBundleFormat = (mappings: Record<string, string>): string[] => {
    return Object.entries(mappings).map(([canisterName, canisterId]) =>
        `${canisterName}:${canisterId}`
    );
};

// Helper function to discover actors from files
const discoverActorsFromFiles = (files: any[]): string[] => {
    if (!files) return [];

    const actorFiles = files.filter(file => {
        if (!file.name.endsWith('.mo')) return false;

        const content = file.content || '';

        // First, check if it's main.mo or Main.mo (regardless of content)
        if (file.name.endsWith('/main.mo') || file.name.endsWith('/Main.mo')) {
            return true;
        }

        // Remove comments first to avoid false positives
        const cleanedContent = content
            .replace(/\/\/.*$/gm, '') // Remove line comments
            .replace(/\/\*[\s\S]*?\*\//g, ''); // Remove block comments

        // More precise regex patterns for actual actor declarations
        const actorPatterns = [
            /^\s*actor\s+\w+\s*(\([^)]*\))?\s*\{/m,      // Named actor at start of line
            /^\s*actor\s+class\s+\w+\s*(\([^)]*\))?\s*\{/m, // Actor class at start of line
            /^\s*actor\s*\{/m,                               // Anonymous actor at start of line
            /^\s*export\s+actor\s+\w+\s*\{/m,                // Exported named actor
            /^\s*export\s+actor\s*\{/m                       // Exported anonymous actor
        ];

        return actorPatterns.some(pattern => pattern.test(cleanedContent));
    });

    return actorFiles.map(f => f.name);
};

// Helper to extract canister name from actor file path
const getCanisterNameFromActor = (actorFile: string): string => {
    const fileName = actorFile.split('/').pop() || actorFile;
    const baseName = fileName.replace('.mo', '');
    if (baseName.toLowerCase() === 'main') return 'main';
    return baseName.toLowerCase();
};

// Helper to create intelligent mappings from available actors and canisters
const createIntelligentMappings = (
    availableActors: string[],
    userCanisters: any[]
): Record<string, string> => {
    const backendCanisters = userCanisters.filter(c => c.canisterType === 'backend');
    const mappings: Record<string, string> = {};

    console.log('🧠 Creating intelligent mappings:', {
        actors: availableActors,
        canisters: backendCanisters.map(c => ({name: c.name, id: c.principal.toText()}))
    });

    // Strategy 1: Match by name
    availableActors.forEach(actorFile => {
        const actorName = getCanisterNameFromActor(actorFile);
        const matchingCanister = backendCanisters.find(c =>
            c.name?.toLowerCase() === actorName ||
            c.name?.toLowerCase().includes(actorName) ||
            actorName.includes(c.name?.toLowerCase() || '')
        );

        if (matchingCanister) {
            mappings[actorName] = matchingCanister.principal.toText();
            console.log(`✅ Name-based match: ${actorFile} -> ${matchingCanister.name}`);
        }
    });

    // Strategy 2: For unmatched actors, use remaining canisters
    const unmatchedActors = availableActors.filter(actor => {
        const actorName = getCanisterNameFromActor(actor);
        return !mappings[actorName];
    });

    const usedCanisterIds = new Set(Object.values(mappings));
    const remainingCanisters = backendCanisters.filter(c =>
        !usedCanisterIds.has(c.principal.toText())
    );

    unmatchedActors.forEach((actorFile, index) => {
        if (remainingCanisters[index]) {
            const canister = remainingCanisters[index];
            const actorName = getCanisterNameFromActor(actorFile);
            mappings[actorName] = canister.principal.toText();
            console.log(`✅ Sequential match: ${actorFile} -> ${canister.name}`);
        }
    });

    return mappings;
};

// 2. MAIN FUNCTION TO GET CANISTER MAPPINGS FOR DEPLOYMENT

const getCanisterMappingsForBundle = (
    projectName: string,
    files: any[],
    userCanisters: any[]
): string[] => {
    console.log(`🔍 Getting canister mappings for bundle deployment...`);

    // Step 1: Try to get existing mappings from localStorage
    const storedMappings = getProjectCanisterMappings(projectName);

    if (Object.keys(storedMappings).length > 0) {
        console.log(`✅ Using stored canister mappings:`, storedMappings);
        return convertMappingsToBundleFormat(storedMappings);
    }

    console.log(`ℹ️ No stored mappings found, creating intelligent mappings...`);

    // Step 2: If no stored mappings, discover actors and create intelligent mappings
    const availableActors = discoverActorsFromFiles(files);

    if (availableActors.length === 0) {
        console.warn(`⚠️ No actors found in project files`);
        return [];
    }

    // Step 3: Create intelligent mappings
    const intelligentMappings = createIntelligentMappings(availableActors, userCanisters);

    if (Object.keys(intelligentMappings).length === 0) {
        console.warn(`⚠️ Could not create intelligent mappings`);
        return [];
    }

    // Step 4: Save the intelligent mappings to localStorage for future use
    Object.entries(intelligentMappings).forEach(([canisterName, canisterId]) => {
        localStorage.setItem(`${projectName}:${canisterName}`, canisterId);
        console.log(`💾 Saved intelligent mapping: ${projectName}:${canisterName} = ${canisterId}`);
    });

    console.log(`🧠 Created and saved intelligent mappings:`, intelligentMappings);
    return convertMappingsToBundleFormat(intelligentMappings);
};


const cleanFilesForBundler = (files: any[], projectName?: string): ProjectFile[] => {
    // NOTE: The bundler service already normalizes paths in normalizeFilesForProjectType()
    // So we should NOT normalize here - just extract content and validate
    // This ensures Monaco's path resolution matches what the bundler receives
    
    const cleaned = files.map(file => ({
        name: file.name, // Keep original path - bundler will normalize it
        content: typeof file.content === 'string' ? file.content : (file.content?.Text || '')
    }));
    
    // Validate files before sending to bundler
    const filesWithEmptyContent = cleaned.filter(f => !f.content || f.content.trim() === '');
    if (filesWithEmptyContent.length > 0) {
        console.warn(`⚠️ Warning: ${filesWithEmptyContent.length} files have empty content:`, 
            filesWithEmptyContent.map(f => f.name));
    }
    
    // Log file count and sizes for debugging
    console.log(`📦 Cleaning ${cleaned.length} files for bundler:`, {
        totalFiles: cleaned.length,
        filesWithContent: cleaned.filter(f => f.content && f.content.trim().length > 0).length,
        filesWithEmptyContent: filesWithEmptyContent.length,
        totalContentSize: cleaned.reduce((sum, f) => sum + (f.content?.length || 0), 0),
        sampleFiles: cleaned.slice(0, 10).map(f => ({ 
            name: f.name, 
            contentLength: f.content?.length || 0,
            hasContent: !!(f.content && f.content.trim().length > 0)
        }))
    });
    
    return cleaned;
};

export type {
    ProjectFile,
    CanisterInfo,
    CompilationStatus,
    BackendDeployParams,
    FrontendDeployParams
};